import { autoCompleteUrlScheme } from '@/utils/helper';

export interface ParsedUrl {
  raw: string;
  normalized: string;
  isValid: boolean;
  isDuplicate: boolean;
  errorKey?: string;
}

// URL 格式正则：支持 http/https 协议，允许通配符 * 和 **
// 主机部分必须含至少一个点（TLD/IP），避免 `not-a-valid-url` 这类无主机单标签字符串被判为合法
const URL_PATTERN = /^https?:\/\/[^\s/$.?#]+\.[^\s/$.?#]+[^\s]*$/i;

/**
 * 归一化 URL 作为去重 key，与后端 normalize_url 对齐：
 * 补全协议头 → 去掉 #fragment → 去掉路径末尾的 `/`（保留 query）。
 * 仅用于判重，不改变实际入库的 URL 值。
 */
export function canonicalizeUrl(url: string): string {
  let u = autoCompleteUrlScheme(url.trim());
  const hashIdx = u.indexOf('#');
  if (hashIdx >= 0) u = u.slice(0, hashIdx);
  const qIdx = u.indexOf('?');
  const query = qIdx >= 0 ? u.slice(qIdx) : '';
  const base = (qIdx >= 0 ? u.slice(0, qIdx) : u).replace(/\/+$/, '');
  return base + query;
}

/**
 * 按 canonical 形式去重，保留首次出现的原始 URL。
 * 返回去重后的列表与被移除的重复条数（用于提示用户）。
 */
export function dedupeByCanonical(urls: string[]): { unique: string[]; removed: number } {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const u of urls) {
    const key = canonicalizeUrl(u);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(u);
  }
  return { unique, removed: urls.length - unique.length };
}

// 常见表头关键词（用于跳过 CSV 首行）
const HEADER_KEYWORDS = new Set(['url', 'link', 'address', 'website', 'urls', 'links', 'uri', 'webpage']);

/**
 * 解析 CSV/TXT 文本内容，提取 URL 列表
 */
export function parseCsvText(text: string): string[] {
  // 去除 BOM
  const cleaned = text.replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/);
  const results: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // 取第一列（处理 CSV 逗号分隔）
    const firstCol = extractFirstColumn(line);
    if (!firstCol) continue;

    // 跳过表头行
    if (i === 0 && HEADER_KEYWORDS.has(firstCol.toLowerCase())) {
      continue;
    }

    results.push(firstCol);
  }

  return results;
}

/**
 * 从 CSV 行中提取第一列的值，处理引号包裹的字段
 */
function extractFirstColumn(line: string): string {
  if (line.startsWith('"')) {
    // 引号包裹的字段：找到配对的闭合引号
    const endQuote = line.indexOf('"', 1);
    if (endQuote > 0) {
      return line.substring(1, endQuote).replace(/""/g, '"').trim();
    }
    // 没有闭合引号，返回去掉首引号后的内容
    return line.substring(1).trim();
  }

  // 非引号字段：取逗号前的部分
  const commaIndex = line.indexOf(',');
  return commaIndex >= 0 ? line.substring(0, commaIndex).trim() : line.trim();
}

/**
 * 使用 ExcelJS 解析 XLSX 文件，提取第一列 URL
 */
export async function parseExcelBuffer(buffer: ArrayBuffer): Promise<string[]> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  if (!worksheet) return [];

  const results: string[] = [];
  let isFirstRow = true;

  worksheet.eachRow((row) => {
    const cellValue = row.getCell(1).text?.trim();
    if (!cellValue) return;

    // 跳过表头
    if (isFirstRow) {
      isFirstRow = false;
      if (HEADER_KEYWORDS.has(cellValue.toLowerCase())) return;
    }

    results.push(cellValue);
  });

  return results;
}

/**
 * 校验 URL 格式（支持路径部分的通配符）
 * 通配符 * 和 ** 仅允许出现在路径段中，不允许出现在域名部分
 */
export function isValidUrl(url: string): boolean {
  if (!url.includes('*')) {
    return URL_PATTERN.test(url);
  }

  // 通配符仅允许在路径部分：拆分域名和路径分别校验
  try {
    // 找到协议后第一个 / 的位置（路径起点）
    const protocolEnd = url.indexOf('://');
    if (protocolEnd < 0) return false;
    const pathStart = url.indexOf('/', protocolEnd + 3);

    // 域名部分不允许通配符
    const domain = pathStart >= 0 ? url.substring(0, pathStart) : url;
    if (domain.includes('*')) return false;

    // 域名部分必须是合法 URL
    const domainUrl = pathStart >= 0 ? domain + '/placeholder' : domain;
    return URL_PATTERN.test(domainUrl);
  } catch {
    return false;
  }
}

/**
 * 校验并标准化 URL 列表，标记重复和无效项
 */
export function validateAndNormalizeUrls(rawUrls: string[], existingUrls: string[]): ParsedUrl[] {
  // 判重 key 用 canonical 形式（与后端一致），避免 trailing-slash / #fragment 变体被放过、后端再静默合并
  const existingSet = new Set(existingUrls.filter((u) => u.trim()).map((u) => canonicalizeUrl(u)));
  const seenInFile = new Set<string>();
  const results: ParsedUrl[] = [];

  for (const raw of rawUrls) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    const normalized = autoCompleteUrlScheme(trimmed);
    const canonical = canonicalizeUrl(trimmed);
    const valid = isValidUrl(normalized);

    let isDuplicate = false;
    let errorKey: string | undefined;

    if (!valid) {
      errorKey = 'create_v2.website.csv_import.status_invalid';
    } else if (existingSet.has(canonical)) {
      isDuplicate = true;
    } else if (seenInFile.has(canonical)) {
      isDuplicate = true;
    }

    if (valid && !isDuplicate) {
      seenInFile.add(canonical);
    }

    results.push({ raw: trimmed, normalized, isValid: valid, isDuplicate, errorKey });
  }

  return results;
}
